/**
 * Infinite Scroll for Shopify Blogs
 * Loads the next page when the end of the article list comes within reach.
 *
 * Same shape as infinite-scroll.js (collections): no scroll listener and no
 * measured page height. An IntersectionObserver watches a one-pixel sentinel
 * placed after the article list, with the lead distance as the observer's
 * bottom margin, and re-evaluates on its own as images grow the page or a
 * page is appended. Nothing here reads a viewport number.
 */

class BlogInfiniteScroll {
  constructor() {
    this.container = document.querySelector('[data-blog-infinite-scroll]');
    if (!this.container) return;

    this.loader = document.querySelector('.blog-infinite-scroll-loader');
    this.endMessage = document.querySelector('.blog-infinite-scroll-end');

    this.currentPage = parseInt(this.container.dataset.currentPage);
    this.totalPages = parseInt(this.container.dataset.totalPages);
    this.sectionId = this.container.dataset.sectionId;
    this.isLoading = false;

    this.init();
  }

  init() {
    // Only initialize if there are more pages
    if (this.currentPage >= this.totalPages) {
      return;
    }

    /* No forced scroll-to-top on back-navigation.
       It used to call window.scrollTo(0, 0) here, which did three things wrong:
       it threw away the browser's own scroll restoration (on an infinitely
       scrolled blog that means losing your place entirely), html has
       scroll-behavior:smooth so the bare two-argument call ANIMATED rather
       than jumping, and being unguarded it fed the header's direction
       detector a large delta. It also read performance.navigation.type,
       removed from the spec. Letting the browser restore the position is
       both correct and free. */

    /* The sentinel: one pixel after the list (articles are appended INTO the
       container, so the sentinel sits after it), never painted over anything.
       It moves down with the list's end as pages are appended. */
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'blog-infinite-scroll-sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    /* display:block inline: base.css hides every empty div (Dawn's :empty
       rule), and an unobservable sentinel loads nothing — found in the
       Simulator, where the observer never fired. */
    this.sentinel.style.cssText = 'display:block;height:1px;margin:0;padding:0;pointer-events:none';
    this.container.insertAdjacentElement('afterend', this.sentinel);
    this.observe();

    /* The lead is a rootMargin, fixed when the observer is made; a rotation
       across the breakpoint gets a fresh one. */
    this.onResize = () => {
      if (this.lead() !== this.leadUsed) this.observe();
    };
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  /* How far below the viewport the end of the list may be before the next
     page is fetched. A phone gets the longer lead so the fetch lands before
     the thumb does. */
  lead() {
    return window.innerWidth < 768 ? 1200 : 800;
  }

  observe() {
    if (this.observer) this.observer.disconnect();
    this.leadUsed = this.lead();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      },
      { rootMargin: `0px 0px ${this.leadUsed}px 0px` }
    );
    this.observer.observe(this.sentinel);
  }

  /* observe() delivers an entry for the current state, so re-observing after
     an append asks the question again. On a tall viewport a freshly appended
     page can leave the sentinel still inside the lead, and an observer only
     speaks when the answer CHANGES. */
  rearm() {
    if (!this.observer || !this.sentinel) return;
    this.observer.unobserve(this.sentinel);
    this.observer.observe(this.sentinel);
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    if (this.sentinel) this.sentinel.remove();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }

  async loadNextPage() {
    // Don't load if already loading or all pages loaded
    if (this.isLoading || this.currentPage >= this.totalPages) {
      return;
    }

    this.isLoading = true;
    this.showLoader();

    try {
      const nextPage = this.currentPage + 1;

      // Build URL for next page
      const url = this.buildNextPageUrl(nextPage);

      const response = await fetch(url);
      const html = await response.text();

      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get the new articles
      const newArticles = doc.querySelectorAll('.blog-articles__article');

      if (newArticles.length > 0) {
        // Append new articles to existing container
        newArticles.forEach(article => {
          this.container.appendChild(article);
        });

        // Update current page
        this.currentPage = nextPage;
        this.container.dataset.currentPage = nextPage;

        // Check if this was the last page
        if (this.currentPage >= this.totalPages) {
          this.showEndMessage();
        }
      }
    } catch (error) {
      console.error('Error loading next blog page:', error);
    } finally {
      this.isLoading = false;
      this.hideLoader();
      if (this.currentPage >= this.totalPages) this.destroy();
      else this.rearm();
    }
  }

  buildNextPageUrl(page) {
    // Get the base blog URL
    const baseUrl = window.location.pathname;

    // Clone current URL params
    const params = new URLSearchParams(window.location.search);

    // Set the page parameter
    params.set('page', page);

    // Add section_id for AJAX loading
    params.set('section_id', this.sectionId);

    return `${baseUrl}?${params.toString()}`;
  }

  showLoader() {
    if (this.loader) {
      this.loader.classList.add('loading');
    }
  }

  hideLoader() {
    if (this.loader) {
      this.loader.classList.remove('loading');
    }
  }

  showEndMessage() {
    if (this.endMessage) {
      this.endMessage.classList.add('visible');
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BlogInfiniteScroll();
  });
} else {
  new BlogInfiniteScroll();
}
